"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState, startTransition } from "react";
import { getApiBasePath, isDayOfServicePath } from "@/app/base-path";
import { LOCAL_STORAGE_KEYS } from "@/constants";
import type { BffFacilityFieldsFragment } from "@/lib/__generated/beffe.sdk";
export interface UserCoordinates {
  latitude: number | null;
  longitude: number | null;
}
interface UseFacilityResolverOptions {
  facilityCodeCookie?: string | null;
  userCoordinates: UserCoordinates;
}
interface FacilityResolverResult {
  currentFacility: BffFacilityFieldsFragment | null;
  setCurrentFacility: (data: BffFacilityFieldsFragment | null) => void;
  nearbyFacilities: BffFacilityFieldsFragment[];
  setNearbyFacilities: (facilities: BffFacilityFieldsFragment[]) => void;
  reloadFacilityByCode: (code: string) => Promise<void>;
  dayOfServiceOnly: boolean;
}
const isFacility = (value: unknown): value is BffFacilityFieldsFragment => typeof value === "object" && value !== null && "code" in value && typeof value.code === "string";
const isFacilityArray = (value: unknown): value is BffFacilityFieldsFragment[] => Array.isArray(value) && value.every(isFacility);
const buildFacilitySelectionCookie = (value: string, maxAge: number) => {
  const secureAttribute = globalThis.location?.protocol === "https:" ? "; Secure" : "";
  return `${LOCAL_STORAGE_KEYS.FACILITY_CODE}=${value}; max-age=${maxAge}; path=/; SameSite=Lax${secureAttribute}`;
};
const getNearbyFacilitiesUrl = (basePath: string, latitude: number, longitude: number, dayOfServiceOnly: boolean) => `${basePath}/api/facilities/nearby/?lat=${latitude}&lng=${longitude}${dayOfServiceOnly ? "&dayOfServiceOnly=true" : ""}`;
const getFacilityUrl = (basePath: string, code: string, dayOfServiceOnly: boolean) => `${basePath}/api/facility/${code}/${dayOfServiceOnly ? "?dayOfServiceOnly=true" : ""}`;
const getPersistedFacilityCode = () => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.FACILITY_CODE);
  } catch {
    return null;
  }
};
const persistFacilitySelection = (facility: BffFacilityFieldsFragment | null) => {
  if (!facility) {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.FACILITY_CODE);
    } catch {}
    document.cookie = buildFacilitySelectionCookie("", 0);
    return;
  }
  try {
    localStorage.setItem(LOCAL_STORAGE_KEYS.FACILITY_CODE, facility.code);
  } catch {}
  document.cookie = buildFacilitySelectionCookie(facility.code, 60 * 60 * 24 * 365);
};
const applyFacilitySelection = (facility: BffFacilityFieldsFragment | null, setCurrentFacility: (data: BffFacilityFieldsFragment | null) => void) => {
  setCurrentFacility(facility);
  persistFacilitySelection(facility);
};
const resolveNearestFacility = (nearby: BffFacilityFieldsFragment[], setCurrentFacility: (data: BffFacilityFieldsFragment | null) => void): BffFacilityFieldsFragment[] => {
  if (nearby.length === 0) {
    applyFacilitySelection(null, setCurrentFacility);
    return [];
  }
  applyFacilitySelection(nearby[0], setCurrentFacility);
  return nearby;
};
interface FetchFacilityParams {
  basePath: string;
  fcParam: string | null;
  facilityCodeCookie: string | null | undefined;
  userLatitude: number | null;
  userLongitude: number | null;
  signal: AbortSignal;
  dayOfServiceOnly: boolean;
  setCurrentFacility: (f: BffFacilityFieldsFragment | null) => void;
}
async function fetchFacility({
  basePath,
  fcParam,
  facilityCodeCookie,
  userLatitude,
  userLongitude,
  signal,
  dayOfServiceOnly,
  setCurrentFacility
}: FetchFacilityParams): Promise<BffFacilityFieldsFragment[]> {
  const rawCode = fcParam ?? facilityCodeCookie ?? getPersistedFacilityCode();
  // Defensively unwrap a JSON-stringified value (e.g. `"4353"`) that may arrive via the
  // ?fc= param, cookie, or localStorage from an upstream source that stringified it.
  const code = rawCode?.replace(/^"(.*)"$/, "$1") ?? rawCode;
  try {
    if (code) {
      // Scenario 1: ?fc= URL param or cookie/localStorage exists (param takes priority).
      const facilityResponse = await fetch(getFacilityUrl(basePath, code, dayOfServiceOnly), {
        signal
      });
      const facilityData: unknown = facilityResponse.ok ? await facilityResponse.json() : null;
      const resolvedFacility = isFacility(facilityData) ? facilityData : null;
      if (resolvedFacility) {
        applyFacilitySelection(resolvedFacility, setCurrentFacility);
        return [];
      }

      // Facility fetch failed / unrecognized code — fall back to picking the nearest
      if (userLatitude == null || userLongitude == null) return [];
      try {
        const nearbyResponse = await fetch(getNearbyFacilitiesUrl(basePath, userLatitude, userLongitude, dayOfServiceOnly), {
          signal
        });
        const nearbyData: unknown = nearbyResponse.ok ? await nearbyResponse.json() : null;
        const nearby = isFacilityArray(nearbyData) ? nearbyData : [];
        return resolveNearestFacility(nearby, setCurrentFacility);
      } catch (error_) {
        if (error_ instanceof Error && error_.name === "AbortError") throw error_;
        return [];
      }
    } else if (userLatitude != null && userLongitude != null) {
      // Scenario 2: no param or cookie, but CloudFront coordinates available
      try {
        const nearbyResponse = await fetch(getNearbyFacilitiesUrl(basePath, userLatitude, userLongitude, dayOfServiceOnly), {
          signal
        });
        if (!nearbyResponse.ok) return [];
        const nearbyData: unknown = await nearbyResponse.json();
        const nearby = isFacilityArray(nearbyData) ? nearbyData : [];
        return resolveNearestFacility(nearby, setCurrentFacility);
      } catch (error_) {
        if (error_ instanceof Error && error_.name === "AbortError") throw error_;
        // Nearby fetch failed — do not overwrite currentFacility; proceed with empty list.
        return [];
      }
    }
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") throw err;
    return [];
  }
  return [];
}
export function useFacilityResolver({
  facilityCodeCookie,
  userCoordinates
}: UseFacilityResolverOptions): FacilityResolverResult {
  const [currentFacility, setCurrentFacility] = useState<BffFacilityFieldsFragment | null>(null);
  const [nearbyFacilities, setNearbyFacilities] = useState<BffFacilityFieldsFragment[]>([]);
  // Seeded from the URL so the very first resolve on /dayofservice is already filtered.
  const [dayOfServiceOnly] = useState(() => isDayOfServicePath());
  const updateFacility = useCallback((facility: BffFacilityFieldsFragment | null) => {
    applyFacilitySelection(facility, setCurrentFacility);
  }, []);
  const reloadFacilityByCode = useCallback(async (code: string) => {
    const basePath = getApiBasePath();
    let response: Response;
    try {
      response = await fetch(getFacilityUrl(basePath, code, dayOfServiceOnly));
    } catch (error_) {
      throw new Error(`Facility swap failed for code ${code}`, {
        cause: error_
      });
    }
    if (!response.ok) {
      throw new Error(`Facility swap failed for code ${code} — upstream ${response.status}`);
    }
    const data: unknown = await response.json();
    if (!isFacility(data)) {
      throw new Error(`Facility swap for code ${code} returned an unexpected shape`);
    }
    applyFacilitySelection(data, setCurrentFacility);
  }, [dayOfServiceOnly]);
  const searchParams = useSearchParams();
  const fcParam = searchParams.get("fc");
  const {
    latitude: userLatitude,
    longitude: userLongitude
  } = userCoordinates;
  useEffect(function LoadFacilityEffect() {
    const basePath = getApiBasePath();
    const controller = new AbortController();
    const {
      signal
    } = controller;
    startTransition(async () => {
      try {
        const newNearbyFacilities = await fetchFacility({
          basePath,
          fcParam,
          facilityCodeCookie,
          userLatitude,
          userLongitude,
          signal,
          dayOfServiceOnly,
          setCurrentFacility
        });
        setNearbyFacilities(newNearbyFacilities);
      } catch {
        // Throwing inside startTransition bubbles to the nearest error boundary — do nothing.
      }
    });
    return () => controller.abort();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- facilityCodeCookie is a server-rendered snapshot; it never changes after hydration.
  [dayOfServiceOnly, fcParam, userLatitude, userLongitude]);

  // Refetch nearby facilities whenever `currentFacility` changes from any source
  // (user picking from the sidebar, machine-driven swap via reloadFacilityByCode, etc.).
  // The initial LoadFacilityEffect above only reacts to fcParam/coords, so without this
  // watcher, nearbyFacilities would go stale after any programmatic facility change.
  useEffect(function RefreshNearbyOnFacilityChangeEffect() {
    if (!currentFacility) return;
    const basePath = getApiBasePath();
    const controller = new AbortController();
    const facilityAtStart = currentFacility;
    const lat = currentFacility.location?.latitude ?? userLatitude;
    const lng = currentFacility.location?.longitude ?? userLongitude;

    // Keep the active office in the list even in day-of-service mode: it was resolved through
    // the filtered endpoints, so it participates, and an empty drawer would leave the patient
    // with nothing to pick and no error to explain why.
    const applyFallback = () => {
      startTransition(() => {
        setNearbyFacilities([facilityAtStart]);
      });
    };
    if (lat == null || lng == null) {
      applyFallback();
      return;
    }
    void (async () => {
      try {
        const response = await fetch(getNearbyFacilitiesUrl(basePath, lat, lng, dayOfServiceOnly), {
          signal: controller.signal
        });
        if (!response.ok) {
          applyFallback();
          return;
        }
        const data: unknown = await response.json();
        const nearby = isFacilityArray(data) ? data : null;
        if (nearby === null) {
          applyFallback();
          return;
        }
        // Ensure the current facility stays in the list even if the nearby endpoint
        // filters it out (e.g. rounding on distance).
        let next;
        if (dayOfServiceOnly) {
          next = nearby;
        } else {
          next = nearby.some(f => f.code === facilityAtStart.code) ? nearby : [facilityAtStart, ...nearby];
        }
        startTransition(() => {
          setNearbyFacilities(next);
        });
      } catch (error_) {
        if (error_ instanceof Error && error_.name === "AbortError") return;
        applyFallback();
      }
    })();
    return () => controller.abort();
  }, [currentFacility, dayOfServiceOnly, userLatitude, userLongitude]);
  return {
    currentFacility,
    setCurrentFacility: updateFacility,
    nearbyFacilities,
    setNearbyFacilities,
    reloadFacilityByCode,
    dayOfServiceOnly
  };
}